import {
	Component,
	EventEmitter,
	HostBinding,
	Input,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { FreudConfirmationService } from '@freud-ds/web-components';
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
import {
	INTERACTION_TYPE,
	ITEM_TYPE,
	SCOPE_TYPE,
	SUBSCOPE_TYPE,
} from 'app/interfaces/actiontracker.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { SubscriptionService } from 'app/services/subscription.service';
import { UserService } from 'app/services/user.service';
import {
	slideDownAnimation,
	slideLeftAnimation,
} from 'app/utils/animations.util';
import { SentryService } from 'modules/core/sentry/sentry.service';
import { CGIBaselineAnswer } from 'modules/promptuary/interfaces/cgi.interface';
import { IAppointment } from 'modules/video-conference/interfaces/appointment.interface';
import { SessionService } from 'modules/video-conference/services/session/session.service';
import { forkJoin, of } from 'rxjs';
import { IContact } from 'shared/interfaces/contact.interface';
import { ContactService } from 'shared/services/contact/contact.service';
import { PromptuaryService } from 'shared/services/promptuary/promptuary.service';
import { UnleashService } from 'shared/unleash/unleash.service';

import { UnifiedPromptuaryService } from './../../../promptuary/components/promptuary-form/unified-promptuary.service';

export enum VCSidebarBreadcrumb {
	SIDEBAR_OPEN = 'sidebar open',
	SIDEBAR_CLOSE = 'sidebar close',
	SIDEBAR_TAB_CHANGE = 'tab change',
	SIDEBAR_PROMPTUARY_CREATE = 'creating promptuary for vc',
}

@Component({
	selector: 'vc-sidebar',
	templateUrl: './vc-sidebar.component.html',
	styleUrls: ['./vc-sidebar.component.scss'],
	animations: [slideLeftAnimation, slideDownAnimation],
	host: {
		'[@slideLeftAnimation]': 'true',
	},
})
export class VCSidebar implements OnInit, OnDestroy {
	activeTabIndex = 0;
	loading = true;
	numberOfSessions: number;
	psychiatrist = false;
	savingData = false;
	sessionContact: IContact | null;
	tempCopiedPrivateNote = '';
	isDocumentaryRecordCopied = false;
	showForm = false;
	serviceEvolution = '';
	documentaryRecordValue = '';
	userData = null;
	currentTab = null;
	isProfessionalPremium = false;
	tabs = [];
	cgiBaselineAnswer: CGIBaselineAnswer;

	@HostBinding('class.error') error = false;

	@Input() appointment: IAppointment;

	@Output('displayStateChange') emDisplayStateChange: EventEmitter<string> =
		new EventEmitter<string>();

	constructor(
		public contactService: ContactService,
		public notificationService: ZkNotificationService,
		public sessionService: SessionService,
		public translateService: TranslateService,
		public userService: UserService,
		protected router: Router,
		public atService: ActionTrackerService,
		public confirmationService: FreudConfirmationService,
		public subscriptionService: SubscriptionService,
		public unleashService: UnleashService,
		public promptuaryService: PromptuaryService,
		public unifiedPromptuaryService: UnifiedPromptuaryService,
		public sentryService: SentryService
	) {}

	ngOnInit(): void {
		this.setBreadcrumb(VCSidebarBreadcrumb.SIDEBAR_OPEN);
		this.createPromptuary();
		this.getVCData();
	}

	ngOnDestroy() {
		this.setBreadcrumb(VCSidebarBreadcrumb.SIDEBAR_CLOSE);
		this.unifiedPromptuaryService.resetPromptuary();
	}

	getVCData() {
		forkJoin({
			contactData: this.appointment.contact.contactId
				? this.contactService.getData(
						this.appointment.contact.contactId.toString()
				  )
				: of(null),
			sessionData: this.sessionService.getData(this.appointment.id),
			userData: this.userService.getProfileData(),
			subscriptions: this.subscriptionService.getSubscriptionList(),
		}).subscribe(
			({ contactData, sessionData, userData, subscriptions }) => {
				this.userData = userData;
				this.isProfessionalPremium = subscriptions.subscriptions.some((sub) => {
					return [
						'active',
						'requested_cancelation',
						'cancelation_accepted',
						'trialing',
					].includes(sub.status);
				});
				this.setTabs();
				this.currentTab = this.tabs[0].value;
				this.sessionContact = contactData;
				this.numberOfSessions = sessionData.numberOfSessions;
				if (contactData?.refUserId) {
					this.getCGIBaselineAnswer(contactData.refUserId);
				}
				this.loading = false;
			},
			({ error }) => {
				this.notificationService.showError(error.messageI18n);
				this.error = true;
			}
		);
	}

	createPromptuary() {
		this.setBreadcrumb(VCSidebarBreadcrumb.SIDEBAR_PROMPTUARY_CREATE);
		this.unifiedPromptuaryService.createPromptuary(
			this.appointment.contact.id,
			this.appointment.id,
			false
		);
	}

	setBreadcrumb(breadcrumb: VCSidebarBreadcrumb, data?: any) {
		this.sentryService.setBreadcrumb(
			breadcrumb,
			'ui.video-conference',
			data ?? {}
		);
	}

	toggleUserForm() {
		this.showForm = !this.showForm;
	}

	changeTab(tabIndex: number) {
		this.setBreadcrumb(VCSidebarBreadcrumb.SIDEBAR_TAB_CHANGE, {
			tab: this.currentTab,
		});
		this.currentTab = this.tabs[tabIndex].value;
		this.atService.eventTracker(
			SCOPE_TYPE.Session,
			SUBSCOPE_TYPE.Conference,
			ITEM_TYPE.Tab,
			INTERACTION_TYPE.Click,
			{ tab: this.currentTab }
		);
		this.activeTabIndex = tabIndex;
	}

	updateClientData(contactData: IContact) {
		this.savingData = true;
		this.sessionContact = contactData;
		this.contactService.updateData(this.sessionContact).subscribe(
			() => {
				this.savingData = false;
				this.toggleUserForm();
				this.notificationService.showSuccess(
					this.translateService.instant('SESSION.SIDEBAR.CLIENT_UPDATED'),
					this.translateService.instant('SESSION.SIDEBAR.SUCCESS')
				);
			},
			({ error }) => {
				this.savingData = false;
				this.notificationService.showError(error.messageI18n);
			}
		);
	}

	getCGIBaselineAnswer(customerId: string): void {
		this.promptuaryService
			.getCGIBaselineAnswer(customerId)
			.subscribe((cgiBaselineAnswer: CGIBaselineAnswer) => {
				this.cgiBaselineAnswer = cgiBaselineAnswer;
			});
	}

	onServiceEvolutionChange(event) {
		this.serviceEvolution = event.newValue;
		this.isDocumentaryRecordCopied =
			event.newValue === this.documentaryRecordValue;
	}

	onDocumentaryRegisterChange(event) {
		this.documentaryRecordValue = event.newValue;
		this.isDocumentaryRecordCopied = event.newValue === this.serviceEvolution;
	}

	setTabs() {
		this.tabs = [
			{
				name: 'SESSION.SIDEBAR.SERVICE_EVOLUTION_TAB',
				value: 'service_evolution',
			},
			{
				name: 'SESSION.SIDEBAR.HISTORY_TAB',
				value: 'historic',
				state: this.isProfessionalPremium ? null : 'disabled',
				tooltip: this.isProfessionalPremium
					? null
					: this.translateService.instant('SESSION.SIDEBAR.PREMIUM_ONLY'),
			},
			{
				name: 'SESSION.SIDEBAR.DOCUMENTARY_RECORD_TAB',
				value: 'documentary_record',
				icon: 'freud-icon-lock',
			},
		];

		if (!this.unleashService.isEnabled('vc-history-tab')) {
			this.tabs.splice(1, 1);
		}

		if (this.userData.mainType === 'psychiatrist') {
			this.tabs.shift();
		}
	}

	copyToDocumentaryRecord() {
		if (
			this.isDocumentaryRecordCopied ||
			this.serviceEvolution === this.documentaryRecordValue
		) {
			return;
		}
		if (this.documentaryRecordValue === '') {
			this.isDocumentaryRecordCopied = true;
			this.tempCopiedPrivateNote = this.serviceEvolution;
		} else {
			this.confirmationService.confirm({
				message: this.translateService.instant('SESSION.SIDEBAR.OVERWRITE'),
				acceptLabel: this.translateService.instant('SESSION.SIDEBAR.ACCEPT'),
				rejectLabel: this.translateService.instant('SESSION.SIDEBAR.REJECT'),
				accept: () => {
					this.isDocumentaryRecordCopied = true;
					this.tempCopiedPrivateNote = this.serviceEvolution;
				},
			});
		}
		setTimeout(() => {
			this.tempCopiedPrivateNote = '';
		}, 1000);
	}
}
